import { FC, ReactElement, useRef } from "react";
import i18next from "i18next";
import { DropTargetMonitor, useDrag, useDrop } from "react-dnd";
import { XYCoord } from "dnd-core";
import { Nav } from "react-bootstrap";
import { TrainingFormValues } from "../features/training-plan/trainingPlan.types";
import { TrainingType } from "../../../shared/utils/enums";

const TRAINING_TAB_TYPE = 'training-tab';

type DraggableTabProps = {
  index: number;
  isActive: boolean;
  training: TrainingFormValues;
  hasError?: boolean;
  moveTab: (dragIndex: number, hoverIndex: number) => void;
  onClick: () => void;
};

const DraggableTab: FC<DraggableTabProps> = ({ index, isActive, training, hasError, moveTab, onClick }: DraggableTabProps): ReactElement => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop({
    accept: TRAINING_TAB_TYPE,
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
    hover(item: { index: number }, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverRect = ref.current.getBoundingClientRect();
      const hoverMiddleX = (hoverRect.right - hoverRect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }

      const hoverClientX = (clientOffset as XYCoord).x - hoverRect.left;
      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX - 10) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX + 10) {
        return;
      }

      moveTab(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: TRAINING_TAB_TYPE,
    item: () => ({ index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));

  const isEndurance = training.trainingKind === TrainingType.ENDURANCE;
  const iconClass = isEndurance ? 'fas fa-person-running' : 'fas fa-dumbbell';

  return (
    <Nav.Item
      ref={ref}
      data-handler-id={handlerId}
      className="flex-shrink-0"
      style={{ opacity: isDragging ? 0.5 : 1, cursor: 'move' }}
    >
      <Nav.Link
        onClick={onClick}
        className="border-0 border-bottom border-3 rounded-0 px-3 py-2 text-nowrap"
        style={{
          color: hasError ? 'var(--bs-danger)' : (isActive ? 'var(--bs-primary)' : '#6c757d'),
          borderColor: isActive ? (hasError ? 'var(--bs-danger)' : 'var(--bs-primary)') : 'transparent',
        }}
      >
        <span className="d-flex align-items-center gap-2">
          <span key={iconClass} className="d-inline-flex flex-shrink-0">
            <i className={iconClass} aria-hidden="true" />
          </span>
          <span className="d-flex flex-column lh-sm text-start">
            <span className="fw-semibold">{training.name}</span>
            <span className={`small ${hasError ? 'text-danger' : 'text-muted'}`}>
              {i18next.t('trainingPlanForm.perWeek', { count: training.workoutsPerWeek ?? 0 })}
            </span>
          </span>
          {hasError && (
            <span className="d-inline-flex flex-shrink-0" title={i18next.t('trainingPlanForm.tabHasErrors')}>
              <i className="fas fa-circle-exclamation" aria-hidden="true" />
              <span className="visually-hidden">{i18next.t('trainingPlanForm.tabHasErrors')}</span>
            </span>
          )}
        </span>
      </Nav.Link>
    </Nav.Item>
  );
};

export default DraggableTab;
